import { reduce } from 'lodash/fp';
import moment from "moment"
import { toJS } from 'mobx';
import {
    E_RISK_TYPE,
    IExportChartConfig,
    RISK_CHART_MODES,
} from '../../services/lib/APIServiceInterfaces';
import { GRAPH_VIEWS, VIEW } from '../../interfaces';
import { CHART_EXPORT_FILE_TYPES, TIME_WINDOW_PRESETS } from '../../constants';
import { getCurrentActiveView } from '../../utils/generalUtils';
import APIService from 'services/APIService';
import countryStore from 'stores/CountryStore';
import chartStore from 'components/GQChart/ChartStore';
import CustomWeightStore from 'stores/CustomWeightStore';
import heatmapStore, { IHeatmapStore } from '../../stores/HeatmapStore';
import risksStore from '../../stores/RisksStore';
import {
    E_GRAPH_DATA_TYPE,
    HEATMAP_SCORE_TYPE_TO_DATA_TYPE,
} from '../Heatmap/HeatmapTypes';
import ExportService from 'services/ExportService';

export const createFileAndDownload = async (
    fileType: CHART_EXPORT_FILE_TYPES,
    chartPayload: IExportChartConfig
) => {
    const response = await APIService.exportChart(fileType, chartPayload);
    const base64Blob = response.data;

    // BE returns a base64 encoded string of one of the following file types: png, csv, zip.
    // When PNG is selected - it returns one PNG file
    // When CSV is selected - it returns one CSV file per indicator type (risk, pulse, currency etc').
    // If user requests several export types, or CSV with several indicator types, he will recieve a zip.

    // 'content-disposition' header includes the response file type & file name.
    // Example header: 'attachment; filename="Chart_fundamental_2025-10-28-2026-05-26.png"'

    const fileName = response.headers['content-disposition'].match(
        /filename=\"(.+[.png|.csv|.zip])\"/
    )[1];
    var a = document.createElement('a');
    a.href = URL.createObjectURL(base64Blob);
    a.download = `${fileName}`;
    a.click();
};

export const generateExportPayload = (
    
    withDisplayData: boolean = true
): IExportChartConfig => {
    const viewType = getCurrentActiveView();
    const baseConfig = {
        viewType,
        hasActiveCustomWeight: CustomWeightStore.hasActiveCustomWeight,
        startDate: chartStore.rangeLeftDate.format('YYYY-MM-DD'),
        endDate: chartStore.rangeRightDate.format('YYYY-MM-DD'),
        virtualToday: chartStore.virtualToday.format('YYYY-MM-DD'),
        frequency: chartStore.compositeDataInterval,
        riskType: viewType === VIEW.HEATMAP ? heatmapStore.riskType : risksStore.currentRisksType,
        riskIds: risksStore.currentList,
        riskChartMode: risksStore.riskChartMode,
        projectionDataOn: risksStore.projectionDataOn,
        currencyOn: risksStore.currencyOn,
        currencyCountry: toJS(risksStore.currencyCountry),
        globalIndexTypes: toJS(risksStore.activeGlobalIndicators),
        macroeconomicsCountry: toJS(risksStore.macroeconomicsCountry),
        countries:
            viewType === VIEW.HEATMAP
                ? heatmapStore.countriesList
                : countryStore.currentCountryList,
        baseDate: chartStore.baseDate.format('YYYY-MM-DD'),
        showVirtualToday: GRAPH_VIEWS.includes(viewType),
    };

    // For CSV
    const graphDataSeries = [];

    if (
        [RISK_CHART_MODES.RISK, RISK_CHART_MODES.RISK_AND_PULSE].includes(
            risksStore.riskChartMode
        )
    ) {
        graphDataSeries.push('composite');
    }
    if (
        [RISK_CHART_MODES.PULSE, RISK_CHART_MODES.RISK_AND_PULSE].includes(
            risksStore.riskChartMode
        )
    ) {
        graphDataSeries.push('pulse');
    }
    if (risksStore.projectionDataOn) {
        graphDataSeries.push('past_projection');
    }

    let globalIndexName = null;
    let globalIndexCountry = null;
    if (risksStore.currencyOn) {
        globalIndexName = 'currency';
        globalIndexCountry = risksStore.currencyCountry[0];
    } else if (risksStore.activeGlobalIndicators.length > 0) {
        globalIndexName = risksStore.activeGlobalIndicators[0];
        globalIndexCountry = risksStore.macroeconomicsCountry[0];
    }
    const csvConfig = {
        graphDataSeries,
        dataResolution: chartStore.compositeDataInterval,
        globalIndexName,
        globalIndexCountry,
    };

    const heatmapConfig: {
        heatmap: IHeatmapStore & {
            numDays: number;
            graphDataType: E_GRAPH_DATA_TYPE;
        };
    } =
        viewType === VIEW.HEATMAP
            ? {
                  heatmap: {
                      startDate: moment(heatmapStore.startDate).format(
                          'YYYY-MM-DD'
                      ),
                      title: heatmapStore.title,
                      selectedCountry: heatmapStore.selectedCountry,
                      selectedRisk: heatmapStore.selectedRisk,
                      timeSpan: heatmapStore.timeSpan,
                      numDays: TIME_WINDOW_PRESETS[heatmapStore.timeSpan].val,
                      scoreType: heatmapStore.scoreType,
                      graphDataType:
                          HEATMAP_SCORE_TYPE_TO_DATA_TYPE[
                              heatmapStore.scoreType
                          ],
                      selectedPercentile: heatmapStore.selectedPercentile,
                  },
              }
            : ({} as any);

    let displayData: any = {};
    if (withDisplayData) {

        // `colorsStack` is used to generate the graph lines colors.
        const countryList = [
            ...countryStore.currentCountryList,
            ...risksStore.currencyCountry,
            ...toJS(risksStore.macroeconomicsCountry),
        ];
        const countryColors = countryStore.serializeColors();
        const riskColors = risksStore.serializeColors();
        const colorsStack = {
            countries: countryColors,
            dimensions: riskColors.dimensionsStack,
            clientFacingIndicators: riskColors.clientFacingIndicatorsStack,
        };
        displayData.colorsStack = colorsStack;

        // `displayNames` is used to generate the graph labels.
        const displayNames = {
            countries: reduce(
                (map: any, countryId: number) => {
                    map[countryId] = toJS(countryStore.countries.allCountries)[
                        countryId
                    ];
                    return map;
                },

                {},
                countryList
            ),
            dimensions: reduce(
                (map: any, dimId: number) => {
                    map[dimId] = toJS(risksStore.dimensions.hashed)[dimId];
                    return map;
                },

                {},
                risksStore.currentRisksType == E_RISK_TYPE.DIMENSIONS
                    ? risksStore.currentList
                    : []
            ),
            clientFacingIndicators: reduce(
                (map: any, cfiId: number) => {
                    map[cfiId] = toJS(risksStore.clientFacingIndicators)[cfiId];
                    return map;
                },

                {},
                risksStore.currentRisksType ==
                    E_RISK_TYPE.CLIENT_FACING_INDICATORS
                    ? risksStore.currentList
                    : []
            ),
            contingentRisks: reduce(
                (map: any, dimId: number) => {
                    map[dimId] = toJS(risksStore.contingentRisks).find(
                        c => c.id === dimId
                    );
                    return map;
                },

                {},
                risksStore.currentRisksType == E_RISK_TYPE.CONTINGENT
                    ? risksStore.currentList
                    : []
            ),
        };
        displayData.displayNames = displayNames;
    }

    const chartPayload: IExportChartConfig = {
        ...baseConfig,
        ...csvConfig,
        ...heatmapConfig,
        ...displayData,
    };
    return chartPayload;
};

export const exportPrediction = async (id: string) => {
    const result = await ExportService.exportPrediction(id);
    const blob = new Blob([result.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prediction_${id}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
};
